import { useEffect, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import { useRecoilCallback, useRecoilValue } from "recoil";

import { ROLL_SHARE_CODES } from "MVVM/GraphQL/matchHistory";
import { AllstarUser, IsUserLoggedIn } from "MVVM/State";
import { LastShareCodeRoll } from "MVVM/State/matchHistory";
import { IAllstarUser } from "MVVM/@types/user";
import { noticeError } from "MVVM/Utilities/NewRelic";

export const SHARE_CODE_ROLL_INTERVAL_MS = 10 * 60 * 1000;

const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll"] as const;

export const useShareCodeRoller = () => {
  const isLoggedIn = useRecoilValue(IsUserLoggedIn);
  const [rollShareCodes] = useLazyQuery(ROLL_SHARE_CODES, {
    fetchPolicy: "network-only",
  });
  const rollShareCodesRef = useRef(rollShareCodes);
  rollShareCodesRef.current = rollShareCodes;

  const handleInteraction = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        // Read at interaction time: getFreshProfile() (e.g. right after the
        // user adds share codes) updates the atom in-session.
        const allstarUser = snapshot
          .getLoadable(AllstarUser)
          .getValue() as unknown as IAllstarUser;
        if (!allstarUser?.user?.hasSharecodes) return;

        const lastRoll = snapshot.getLoadable(LastShareCodeRoll).getValue();
        const at = Date.now();
        if (lastRoll && at - lastRoll.at < SHARE_CODE_ROLL_INTERVAL_MS) return;

        // Recorded at request time, success or failure, so a failed roll
        // still waits out the full interval before the next attempt.
        set(LastShareCodeRoll, { at, source: "auto" });

        rollShareCodesRef
          .current()
          .then((result) => {
            if (result?.error)
              noticeError(
                `Background share code roll failed: ${result.error.message}`,
              );
            set(LastShareCodeRoll, (current) =>
              // A manual roll may have overwritten us while in flight.
              current?.source === "auto" && current.at === at
                ? { ...current, status: result?.data?.rollShareCodes?.status }
                : current,
            );
          })
          .catch((error) => {
            noticeError(`Background share code roll failed: ${error}`);
          });
      },
    [],
  );

  useEffect(() => {
    if (!isLoggedIn) return;

    const options: AddEventListenerOptions = { passive: true, capture: true };
    INTERACTION_EVENTS.forEach((eventType) =>
      window.addEventListener(eventType, handleInteraction, options),
    );
    return () =>
      INTERACTION_EVENTS.forEach((eventType) =>
        window.removeEventListener(eventType, handleInteraction, options),
      );
  }, [isLoggedIn, handleInteraction]);
};

export const ShareCodeRoller = () => {
  useShareCodeRoller();
  return null;
};
